import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createChatSession, sendGuestMessage, getChatMessages } from '../api/client';
import Header from '../components/Header';
import './GuestChat.css';

export default function GuestChat() {
  const [searchParams] = useSearchParams();
  const landlordSlug = searchParams.get('to') || 'ivan'; // ?to=ivan

  const [sessionId, setSessionId] = useState(() => localStorage.getItem('chat_session_' + landlordSlug));
  const [landlordName, setLandlordName] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [started, setStarted] = useState(!!sessionId);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Poll for new messages every 5 seconds
  useEffect(() => {
    if (!sessionId) return;

    async function poll() {
      try {
        const lastId = messages.length > 0 ? messages[messages.length - 1].id : undefined;
        const data = await getChatMessages(sessionId, lastId);
        if (data.messages && data.messages.length > 0) {
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const newMsgs = data.messages.filter(m => !existingIds.has(m.id));
            return newMsgs.length > 0 ? [...prev, ...newMsgs] : prev;
          });
        }
      } catch (err) {
              if (err.response?.status === 404) {
                // Session was deleted — reset
                localStorage.removeItem('chat_session_' + landlordSlug);
                setSessionId(null);
                setStarted(false);
                setMessages([]);
                clearInterval(pollRef.current);
              }
            }
    }

    poll(); // immediate first fetch
    pollRef.current = setInterval(poll, 5000);
    return () => clearInterval(pollRef.current);
  }, [sessionId]); // eslint-disable-line

  async function handleStartChat(e) {
    e.preventDefault();
    if (!guestName.trim()) return;

    try {
      const data = await createChatSession(landlordSlug, guestName, guestPhone);
      setSessionId(data.session_id);
      setLandlordName(data.landlord_name);
      localStorage.setItem('chat_session_' + landlordSlug, data.session_id);
      setStarted(true);
    } catch (err) {
      alert('Не удалось начать чат. Попробуйте позже.');
    }
  }

  async function handleSend(e) {
    e.preventDefault();
    if (!input.trim() || sending) return;

    setSending(true);
    try {
      const msg = await sendGuestMessage(sessionId, input.trim());
      setMessages(prev => [...prev, msg]);
      setInput('');
    } catch (err) {
      alert('Ошибка отправки сообщения');
    } finally {
      setSending(false);
    }
  }

  if (!started) {
    return (
      <>
        <Header />
        <div className="chat-start-page">
          <div className="chat-start-card card">
            <div className="chat-start-icon">💬</div>
            <h2>Написать арендодателю</h2>
            <p className="chat-start-sub">Представьтесь, чтобы начать переписку</p>
            <form onSubmit={handleStartChat}>
              <div className="form-group">
                <input
                  type="text"
                  className="form-input"
                  placeholder="Ваше имя *"
                  value={guestName}
                  onChange={e => setGuestName(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <input
                  type="tel"
                  className="form-input"
                  placeholder="Телефон (необязательно)"
                  value={guestPhone}
                  onChange={e => setGuestPhone(e.target.value)}
                />
              </div>
              <button type="submit" className="btn btn-primary btn-full">
                Начать чат
              </button>
            </form>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="chat-page">
        <div className="chat-container card">
          <div className="chat-header">
            <span className="chat-header-icon">💬</span>
            <div>
              <div className="chat-header-name">{landlordName || 'Арендодатель'}</div>
              <div className="chat-header-status">Обычно отвечает в течение часа</div>
            </div>
          </div>

          <div className="chat-messages">
            {messages.length === 0 && (
              <div className="chat-empty">Напишите ваш вопрос — арендодатель ответит в ближайшее время</div>
            )}
            {messages.map(msg => (
              <div key={msg.id} className={`chat-msg ${msg.direction === 'guest' ? 'chat-msg-mine' : 'chat-msg-theirs'}`}>
                <div className="chat-bubble">
                  {msg.text}
                  <span className="chat-time">
                    {new Date(msg.created_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <form className="chat-input-bar" onSubmit={handleSend}>
            <input
              type="text"
              className="chat-input"
              placeholder="Введите сообщение..."
              value={input}
              onChange={e => setInput(e.target.value)}
              disabled={sending}
              autoFocus
            />
            <button type="submit" className="chat-send-btn" disabled={!input.trim() || sending}>
              ➤
            </button>
          </form>
        </div>
      </div>
    </>
  );
}
